import {
  checklistCatalogDeliveryCanonicalJson,
  CHECKLIST_CATALOG_IMPACT_LIMITS,
  CHECKLIST_CATALOG_IMPACT_POLICY,
} from '@tcvs/shared';
import type {
  ChecklistCatalogDeliveryBaseline,
  ChecklistCatalogImpactReportDto,
  ChecklistCatalogImpactDiff,
  ChecklistCatalogImpactReason,
} from '@tcvs/shared';
const baseline = (value: ChecklistCatalogDeliveryBaseline | null) =>
  value === null
    ? null
    : {
        snapshotFingerprint: value.snapshotFingerprint,
        sourceBinding: value.sourceBinding,
        checklistTableId: value.checklistTableId,
        catalogRevision: value.catalogRevision,
        correctionRevision: value.correctionRevision,
        retainedStateDigest: value.retainedStateDigest,
        savedReferenceDigest: value.savedReferenceDigest,
        policyVersion: value.policyVersion,
      };
const reasons = (values: ChecklistCatalogImpactReason[]) =>
  values.map((value) => ({
    code: value.code,
    detail: value.detail,
    ...(value.fieldPath === undefined ? {} : { fieldPath: value.fieldPath }),
    ...(value.sourceItemId === undefined ? {} : { sourceItemId: value.sourceItemId }),
    ...(value.sourceSubsetId === undefined ? {} : { sourceSubsetId: value.sourceSubsetId }),
    ...(value.definitionId === undefined ? {} : { definitionId: value.definitionId }),
  }));
const diff = (value: ChecklistCatalogImpactDiff) =>
  Object.fromEntries(
    (['roster', 'parallelDefinitions', 'subsetDefinitions'] as const).map((key) => [
      key,
      {
        adds: value[key].adds,
        updates: value[key].updates,
        unchanged: value[key].unchanged,
        sourceMissing: value[key].sourceMissing,
        sourceMissingDisposition: value[key].sourceMissingDisposition,
      },
    ]),
  );
/** Explicit DTO projection keeps raw inputs, upload credentials and future private fields out of downloads. */
export function catalogImpactReportDownload(dto: ChecklistCatalogImpactReportDto) {
  const r = dto.report;
  const counts = (value: typeof r.input.claimedCounts) => ({
    roster: value.roster,
    parallelDefinitions: value.parallelDefinitions,
    subsetDefinitions: value.subsetDefinitions,
  });
  const coverage = (value: typeof r.coverage) => ({
    catalog: value.catalog,
    corrections: value.corrections,
    savedReferences: value.savedReferences,
  });
  return {
    report: {
      schemaVersion: r.schemaVersion,
      reportType: r.reportType,
      policyVersion: r.policyVersion,
      reportId: r.reportId,
      digest: r.digest,
      deliveryId: r.deliveryId,
      manifestDigest: r.manifestDigest,
      sourceSystem: r.sourceSystem,
      sourceTargetId: r.sourceTargetId,
      execution: r.execution,
      catalogComparison: r.catalogComparison,
      eligibility: r.eligibility,
      applyAllowed: r.applyAllowed,
      verification: r.verification,
      input: {
        transportSha256: r.input.transportSha256,
        rawSha256: r.input.rawSha256,
        transportBytes: r.input.transportBytes,
        rawBytes: r.input.rawBytes,
        claimedCounts: counts(r.input.claimedCounts),
        computedCounts: counts(r.input.computedCounts),
        claimedFingerprint: r.input.claimedFingerprint,
        computedFingerprint: r.input.computedFingerprint,
      },
      baseline: {
        claimedDigest: r.baseline.claimedDigest,
        observed: baseline(r.baseline.observed),
        checks: r.baseline.checks.map((check) => ({ field: check.field, result: check.result })),
      },
      coverage: coverage(r.coverage),
      providerDiff: diff(r.providerDiff),
      effectiveDiff: diff(r.effectiveDiff),
      correctionImpact: {
        coverage: r.correctionImpact.coverage,
        active: r.correctionImpact.active,
        replayed: r.correctionImpact.replayed,
        conflicts: r.correctionImpact.conflicts,
      },
      dependencyCoverage: Object.fromEntries(
        (
          [
            'authoritativeReferences',
            'draftSelections',
            'reviewChecklistSelections',
            'selectedDefinitions',
          ] as const
        ).map((key) => [
          key,
          { coverage: r.dependencyCoverage[key].coverage, count: r.dependencyCoverage[key].count },
        ]),
      ),
      reasons: reasons(r.reasons),
      totalReasonCount: r.totalReasonCount,
      reasonsTruncated: r.reasonsTruncated,
      bounds: {
        maxRawBytes: r.bounds.maxRawBytes,
        maxRoster: r.bounds.maxRoster,
        maxReferences: r.bounds.maxReferences,
        maxDefinitionsPerKind: r.bounds.maxDefinitionsPerKind,
        maxOverlays: r.bounds.maxOverlays,
        maxImportLedger: r.bounds.maxImportLedger,
        queryPageSize: r.bounds.queryPageSize,
        maxCommands: r.bounds.maxCommands,
        maxDatabaseBytes: r.bounds.maxDatabaseBytes,
        maxMilliseconds: r.bounds.maxMilliseconds,
        maxReportBytes: r.bounds.maxReportBytes,
        maxReasons: r.bounds.maxReasons,
      },
      work: {
        commands: r.work.commands,
        databaseBytes: r.work.databaseBytes,
        elapsedMilliseconds: r.work.elapsedMilliseconds,
      },
      createdAt: r.createdAt,
      expiresAt: r.expiresAt,
    },
    freshness: {
      status: dto.freshness.status,
      checkedAt: dto.freshness.checkedAt,
      reasons: reasons(dto.freshness.reasons),
    },
  };
}
export function downloadCatalogImpactReports(
  reports: ChecklistCatalogImpactReportDto[],
  filename: string,
) {
  const output = reports.map(catalogImpactReportDownload);
  const url = URL.createObjectURL(
    new Blob(
      [
        JSON.stringify(
          output.length === 1
            ? output[0]
            : { schemaVersion: 1, reportType: 'CATALOG_DRY_RUN_COLLECTION', reports: output },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    ),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Validate the exact known envelope, then verify immutable content rather than trusting a digest label. */
export async function verifyCatalogImpactReportDto(value: unknown): Promise<boolean> {
  try {
    const dto = value as ChecklistCatalogImpactReportDto;
    const r = dto.report;
    const integer = (n: unknown): n is number => Number.isSafeInteger(n) && Number(n) >= 0;
    const count = (n: unknown) => n === null || integer(n);
    const hash = (s: unknown) => typeof s === 'string' && /^[a-f0-9]{64}$/.test(s);
    const token = (s: unknown) => typeof s === 'string' && /^[A-Za-z0-9_:?.#-]{1,128}$/.test(s);
    const date = (s: unknown) =>
      typeof s === 'string' && /^\d{4}-\d\d-\d\dT/.test(s) && Number.isFinite(Date.parse(s));
    const fp = (s: unknown) => typeof s === 'string' && /^[a-f0-9]{16}$/.test(s);
    const reasonValid = (reason: ChecklistCatalogImpactReason) =>
      typeof reason.code === 'string' &&
      /^[A-Za-z0-9_-]{1,96}$/.test(reason.code) &&
      typeof reason.detail === 'string' &&
      reason.detail.length <= 1024 &&
      !/https?:|s3:/.test(reason.detail) &&
      (reason.fieldPath === undefined ||
        (typeof reason.fieldPath === 'string' &&
          /^\/[A-Za-z0-9/_-]{0,255}$/.test(reason.fieldPath))) &&
      [reason.sourceItemId, reason.sourceSubsetId].every(
        (id) => id === undefined || (typeof id === 'string' && /^[0-9]{1,64}$/.test(id)),
      ) &&
      (reason.definitionId === undefined ||
        /^azure-sql-ebay#[0-9]{1,64}#[0-9]{1,64}$/.test(reason.definitionId));
    const coverageValid = (coverage: typeof r.coverage) =>
      ['COMPLETE', 'PARTIAL', 'NOT_RUN'].includes(coverage.catalog) &&
      ['COMPLETE', 'PARTIAL', 'NOT_RUN'].includes(coverage.corrections) &&
      coverage.savedReferences === 'UNKNOWN';
    if (
      r.schemaVersion !== 1 ||
      r.reportType !== 'CATALOG_DRY_RUN' ||
      r.policyVersion !== CHECKLIST_CATALOG_IMPACT_POLICY ||
      !hash(r.reportId) ||
      !hash(r.digest) ||
      !hash(r.manifestDigest) ||
      !token(r.deliveryId) ||
      r.sourceSystem !== 'azure-sql-ebay' ||
      !/^[1-9][0-9]{0,31}$/.test(r.sourceTargetId) ||
      !['COMPLETED', 'FAILED'].includes(r.execution) ||
      !['COMPLETE', 'PARTIAL', 'NOT_RUN'].includes(r.catalogComparison) ||
      r.eligibility !== 'BLOCKED' ||
      r.applyAllowed !== false ||
      r.verification !== 'NOT_RUN'
    )
      return false;
    if (
      !hash(r.input.transportSha256) ||
      !hash(r.input.rawSha256) ||
      !integer(r.input.transportBytes) ||
      !integer(r.input.rawBytes) ||
      !fp(r.input.claimedFingerprint) ||
      !(r.input.computedFingerprint === null || fp(r.input.computedFingerprint)) ||
      !Object.values(r.input.claimedCounts).every(count) ||
      !Object.values(r.input.computedCounts).every(count)
    )
      return false;
    if (
      !hash(r.baseline.claimedDigest) ||
      !coverageValid(r.coverage) ||
      !Array.isArray(r.baseline.checks) ||
      !r.baseline.checks.every(
        (check) =>
          [
            'snapshotFingerprint',
            'sourceBinding',
            'checklistTableId',
            'catalogRevision',
            'correctionRevision',
            'retainedStateDigest',
            'savedReferenceDigest',
            'policyVersion',
          ].includes(check.field) && ['MATCH', 'MISMATCH', 'UNVERIFIED'].includes(check.result),
      )
    )
      return false;
    if (
      r.baseline.observed &&
      (!fp(r.baseline.observed.snapshotFingerprint) ||
        !hash(r.baseline.observed.retainedStateDigest) ||
        !hash(r.baseline.observed.savedReferenceDigest) ||
        ![
          'sourceBinding',
          'checklistTableId',
          'catalogRevision',
          'correctionRevision',
          'policyVersion',
        ].every((key) =>
          token(r.baseline.observed![key as keyof ChecklistCatalogDeliveryBaseline]),
        ))
    )
      return false;
    for (const differences of [r.providerDiff, r.effectiveDiff])
      for (const key of ['roster', 'parallelDefinitions', 'subsetDefinitions'] as const)
        if (
          differences[key].sourceMissingDisposition !== 'RETAINED' ||
          !['adds', 'updates', 'unchanged', 'sourceMissing'].every((field) =>
            count(differences[key][field as 'adds']),
          )
        )
          return false;
    if (
      !['COMPLETE', 'PARTIAL', 'NOT_RUN'].includes(r.correctionImpact.coverage) ||
      ![r.correctionImpact.active, r.correctionImpact.replayed, r.correctionImpact.conflicts].every(
        count,
      ) ||
      !Object.values(r.dependencyCoverage).every(
        (item) => item.coverage === 'UNKNOWN' && item.count === null,
      )
    )
      return false;
    if (
      !Array.isArray(r.reasons) ||
      r.reasons.length > CHECKLIST_CATALOG_IMPACT_LIMITS.maxReasons ||
      !r.reasons.every(reasonValid) ||
      !integer(r.totalReasonCount) ||
      r.totalReasonCount < r.reasons.length ||
      typeof r.reasonsTruncated !== 'boolean' ||
      r.reasonsTruncated !== r.totalReasonCount > r.reasons.length ||
      !date(r.createdAt) ||
      !date(r.expiresAt) ||
      !Object.values(r.work).every(integer)
    )
      return false;
    if (
      !['CURRENT', 'STALE', 'UNKNOWN'].includes(dto.freshness.status) ||
      !date(dto.freshness.checkedAt) ||
      !Array.isArray(dto.freshness.reasons) ||
      dto.freshness.reasons.length > CHECKLIST_CATALOG_IMPACT_LIMITS.maxReasons ||
      !dto.freshness.reasons.every(reasonValid)
    )
      return false;
    if (
      checklistCatalogDeliveryCanonicalJson(r.bounds) !==
      checklistCatalogDeliveryCanonicalJson(CHECKLIST_CATALOG_IMPACT_LIMITS)
    )
      return false;
    const projected = catalogImpactReportDownload(dto);
    const canonical = checklistCatalogDeliveryCanonicalJson(dto);
    if (
      new TextEncoder().encode(canonical).length >
        CHECKLIST_CATALOG_IMPACT_LIMITS.maxReportBytes + 64 * 1024 ||
      checklistCatalogDeliveryCanonicalJson(projected) !== canonical
    )
      return false;
    const { digest, ...unsigned } = projected.report;
    const actual = Array.from(
      new Uint8Array(
        await crypto.subtle.digest(
          'SHA-256',
          new TextEncoder().encode(checklistCatalogDeliveryCanonicalJson(unsigned)),
        ),
      ),
      (byte) => byte.toString(16).padStart(2, '0'),
    ).join('');
    return digest === actual;
  } catch {
    return false;
  }
}
